// src/lib/backup.js — backup engine (§5): fetch blobs + manifest + jszip bundle

import JSZip from 'jszip';

/**
 * Fetch one blob as a Blob.
 * @param {Function} getBlob async ({did, cid}) => {data: Blob|ArrayBuffer, headers}
 * @returns {Promise<{blob: Blob, mimeType: string, size: number}>}
 */
export async function fetchBlob(getBlob, { did, cid, mimeType = 'application/octet-stream' }) {
  const res = await getBlob({ did, cid });
  let data = res?.data;

  if (data instanceof Blob) {
    return { blob: data, mimeType: data.type || mimeType, size: data.size };
  }

  if (data instanceof ArrayBuffer) {
    return {
      blob: new Blob([data], { type: mimeType }),
      mimeType,
      size: data.byteLength,
    };
  }

  // Uint8Array / Buffer fallback
  if (data && data.byteLength !== undefined && data.byteLength !== null) {
    return {
      blob: new Blob([data], { type: mimeType }),
      mimeType,
      size: data.byteLength,
    };
  }

  throw new Error(`getBlob returned unexpected data for ${cid}`);
}

/**
 * Build a zip backup: blobs/ + manifest.json.
 * @param {Array<{cid: string, blob: Blob, mimeType: string, size: number, url: string}>} items
 * @returns {Promise<Blob>} the zip as a Blob
 */
export async function buildBackupZip(items) {
  const zip = new JSZip();

  const manifest = items.map((it) => ({
    cid: it.cid,
    mimeType: it.mimeType,
    size: it.size,
    url: it.url,
  }));

  for (const it of items) {
    zip.file(`blobs/${it.cid}`, it.blob);
  }

  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  return zip.generateAsync({ type: 'blob', compression: 'STORE' });
}

/**
 * Trigger a browser download of a Blob.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export default fetchBlob;
